import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TaxBreakdownPopover from './TaxBreakdownPopover';
import useCurrency from '../../../../components/CurrencyProvider';

export interface SummaryCardProps {
  total: number;
  concession: number;
  tax: number;
  net: number;
  consumeFromPackage?: number;
  /** Total of services from package (before capping) */
  packageServicesTotal?: number;
  /** Available package service limit */
  packageServiceLimit?: number;
  /** Bill type - skip package limit validation when 'Package' */
  billType?: string;
  /** When true, hide "Consume from Package" section (used when closing a package) */
  isClosePackageMode?: boolean;
  /** Advance balance amount from active packages (shown when billType is not 'Package') */
  advanceBalanceAmount?: number;
}

/**
 * Text constants to avoid hardcoding and make i18n easier later.
 */
const TEXT = {
  totalAmount: 'Total Amount',
  consumeFromPackage: 'Consume from Package',
  concessionAmount: 'Concession Amount',
  taxAmount: 'Tax Amount',
  netAmount: 'Net Amount',
  advanceBalanceAmount: 'Advance Balance Amount',
  requiredAmount: 'Required Amount',
  taxInfoTooltip: 'View detailed tax breakdown',
  taxPopoverTitle: 'Tax Amount',
  netInfoTooltip: 'View net amount breakdown',
  netPopoverTitle: 'Net Amount Breakdown',
} as const;

/**
 * Style constants to avoid inline objects in JSX.
 * Layout and basic look remain the same; we only add subtle transitions.
 */
const STYLES = {
  card: {
    p: 2.5,
    background: '#fafbfc',
    borderRadius: 3,
    boxShadow: 'none',
    transition: 'box-shadow 150ms ease, transform 150ms ease',
    '&:hover, &:focus-within': {
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1.5,
  },
  taxRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  taxLabelContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  taxDivider: {
    height: 2,
    background: '#E2E3E5',
    mt: 1,
    borderRadius: 1,
  },
  netRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  netLabelContainer: {
    display: 'flex',
    alignItems: 'center',
  },
} as const;

const SummaryCard: React.FC<SummaryCardProps> = ({
  total,
  concession,
  tax,
  net,
  consumeFromPackage = 0,
  packageServicesTotal = 0,
  packageServiceLimit = 0,
  billType = '',
  isClosePackageMode = false,
  advanceBalanceAmount = 0,
}) => {
  const [taxAnchorEl, setTaxAnchorEl] = useState<HTMLElement | null>(null);
  const [taxOpen, setTaxOpen] = useState(false);
  const [netAnchorEl, setNetAnchorEl] = useState<HTMLElement | null>(null);
  const [netOpen, setNetOpen] = useState(false);
  const { formatCurrency } = useCurrency();

  // Check if package limit is exceeded
  // Skip validation when billType is 'Package' (closing package invoice flow)
  const isPackageLimitExceeded = billType !== 'Package' && packageServicesTotal > 0 && packageServiceLimit > 0 && packageServicesTotal > packageServiceLimit;
  const exceededAmount = isPackageLimitExceeded ? packageServicesTotal - packageServiceLimit : 0;

  // Memoize tax items to avoid re-creating on every render.
  const taxItems = useMemo(
    () => [
      { label: 'Sales Tax (3%)', value: formatCurrency(6.00) },
      { label: 'VAT (2%)', value: formatCurrency(4.00) },
    ],
    [formatCurrency]
  );

  // Memoize net breakdown items
  const netBreakdownItems = useMemo(
    () => {
      const items: { label: string; value: string; isBold?: boolean }[] = [
        { label: 'Total Charges', value: formatCurrency(total) },
        { label: 'Less: Concession', value: `- ${formatCurrency(concession)}` },
        { label: 'Taxable Amount', value: formatCurrency(total - concession) },
        { label: 'Add: Tax', value: `+ ${formatCurrency(tax)}` },
      ];
      
      if (consumeFromPackage > 0) {
        items.push({ label: 'Less: Consume from Package', value: `- ${formatCurrency(consumeFromPackage)}` });
        items.push({ label: 'Net Amount', value: formatCurrency(Math.max(0, net - consumeFromPackage)), isBold: true });
      } else {
        items.push({ label: 'Net Amount', value: formatCurrency(net), isBold: true });
      }
      
      return items;
    },
    [total, concession, tax, net, consumeFromPackage, formatCurrency]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTaxToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget as HTMLElement;

      setTaxOpen((prevOpen) => !(prevOpen && taxAnchorEl === target));
      setTaxAnchorEl((prevAnchor) =>
        prevAnchor && prevAnchor === target ? null : target
      );
    },
    [taxAnchorEl]
  );

  const handleTaxClose = useCallback(() => {
    setTaxOpen(false);
    setTaxAnchorEl(null);
  }, []);

  const handleNetToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget as HTMLElement;

      setNetOpen((prevOpen) => !(prevOpen && netAnchorEl === target));
      setNetAnchorEl((prevAnchor) =>
        prevAnchor && prevAnchor === target ? null : target
      );
    },
    [netAnchorEl]
  );

  const handleNetClose = useCallback(() => {
    setNetOpen(false);
    setNetAnchorEl(null);
  }, []);

  // Memoize formatted values
  const formattedValues = useMemo(
    () => ({
      total: formatCurrency(total),
      consumeFromPackage: formatCurrency(consumeFromPackage),
      concession: formatCurrency(concession),
      tax: formatCurrency(tax),
      net: formatCurrency(net),
      // Net amount after deducting package consumption (payable amount)
      netAfterPackage: formatCurrency(Math.max(0, net - consumeFromPackage)),
      advanceBalanceAmount: formatCurrency(advanceBalanceAmount),
    }),
    [total, consumeFromPackage, concession, tax, net, advanceBalanceAmount, formatCurrency]
  );

  return (
    <Card
      elevation={0}
      sx={STYLES.card}
      // Card is still just a container; keep semantics the same.
      aria-label="Summary of charges"
      tabIndex={-1}
    >
      {/* Total Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="subtitle2">{TEXT.totalAmount}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formattedValues.total}
        </Typography>
      </Box>

      {/* Package Limit Warning - Show when exceeded */}
      {isPackageLimitExceeded && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            backgroundColor: '#fff3e0',
            borderRadius: '8px',
            border: '1px solid #ffb74d',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <WarningAmberIcon sx={{ fontSize: 18, color: '#ed6c02', mt: 0.25 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
                Package Limit Exceeded
              </Typography>
              <Typography variant="caption" sx={{ color: '#5d4037', display: 'block', lineHeight: 1.4 }}>
                Services total ({formatCurrency(packageServicesTotal)}) exceeds available limit ({formatCurrency(packageServiceLimit)}) by <strong>{formatCurrency(exceededAmount)}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: '#5d4037', display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                💡 <strong>To proceed:</strong> Uncheck some package services or remove them from package to charge at regular tariff rates.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Concession Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="subtitle2">{TEXT.concessionAmount}</Typography>
        <Typography variant="body2">{formattedValues.concession}</Typography>
      </Box>

      {/* Tax Amount & Breakdown */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={STYLES.taxRow}>
          <Box sx={STYLES.taxLabelContainer}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>
              {TEXT.taxAmount}
            </Typography>

            {/* Hidden temporarily - we are doing this service configuration later
            <Tooltip title={TEXT.taxInfoTooltip} arrow enterTouchDelay={0}>
              <IconButton
                onClick={handleTaxToggle}
                size="small"
                sx={{ p: 0, ml: 0.5, cursor: 'pointer', color: '#5A8AEA' }}
                aria-label={TEXT.taxInfoTooltip}
                aria-haspopup="dialog"
                aria-expanded={taxOpen ? 'true' : 'false'}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
             */}

            <TaxBreakdownPopover
              title={TEXT.taxPopoverTitle}
              items={taxItems}
              anchorEl={taxAnchorEl}
              open={taxOpen}
              onClose={handleTaxClose}
            />
          </Box>

          <Typography variant="body2">{formattedValues.tax}</Typography>
        </Box>

        <Box sx={STYLES.taxDivider} />
      </Box>

      {/* Advance Balance Amount - Show when billType is not 'Package' */}
      {billType !== 'Package' && (
        <Box sx={STYLES.row}>
          <Typography variant="subtitle2">{TEXT.advanceBalanceAmount}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: advanceBalanceAmount > 0 ? '#4caf50' : undefined }}>
            {formattedValues.advanceBalanceAmount}
          </Typography>
        </Box>
      )}

      {/* Required Amount - Show when billType is not 'Package' and advance exists */}
      {/* {billType !== 'Package' && advanceBalanceAmount > 0 && (
        <Box sx={STYLES.row}>
          <Typography variant="subtitle2">{TEXT.requiredAmount}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: net > advanceBalanceAmount ? '#ed6c02' : '#4caf50' }}>
            {formatCurrency(Math.max(0, net - advanceBalanceAmount))}
          </Typography>
        </Box>
      )} */}

      {/* Consume from Package - with limit info (hidden in close package mode) */}
      {!isClosePackageMode && (consumeFromPackage > 0 || packageServicesTotal > 0) && (
        <Box sx={STYLES.row}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle2">Consume from Package</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: isPackageLimitExceeded ? '#ed6c02' : '#4caf50' 
              }}
            >
              {formattedValues.consumeFromPackage}
            </Typography>
            {isPackageLimitExceeded && (
              <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block' }}>
                Limit: {formatCurrency(packageServiceLimit)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Net Amount */}
      <Box sx={STYLES.netRow}>
        <Box sx={STYLES.netLabelContainer}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {TEXT.netAmount}
          </Typography>

          <Tooltip title={TEXT.netInfoTooltip} arrow enterTouchDelay={0}>
            <IconButton
              onClick={handleNetToggle}
              size="small"
              sx={{ p: 0, ml: 1, mt: 0.5, cursor: 'pointer', color: '#5A8AEA' }}
              aria-label={TEXT.netInfoTooltip}
              aria-haspopup="dialog"
              aria-expanded={netOpen ? 'true' : 'false'}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <TaxBreakdownPopover
            title={TEXT.netPopoverTitle}
            items={netBreakdownItems}
            anchorEl={netAnchorEl}
            open={netOpen}
            onClose={handleNetClose}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {consumeFromPackage > 0 ? formattedValues.netAfterPackage : formattedValues.net}
        </Typography>
      </Box>
    </Card>
  );
};

export default SummaryCard;
